import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Match } from '../../types';

export interface PenaltyWinnerRequest {
  match: Match;
  scoreA: number;
  scoreB: number;
}

interface KnockoutPenaltyWinnerDialogProps {
  request: PenaltyWinnerRequest | null;
  isOpen: boolean;
  isLoading?: boolean;
  onConfirm: (winnerCode: string) => void;
  onCancel: () => void;
}

export const KnockoutPenaltyWinnerDialog: React.FC<KnockoutPenaltyWinnerDialogProps> = ({
  request,
  isOpen,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [selectedCode, setSelectedCode] = useState('');

  useEffect(() => {
    if (request) {
      setSelectedCode(request.match.winner_code ?? '');
    } else {
      setSelectedCode('');
    }
  }, [request]);

  if (!request) return null;

  const { match, scoreA, scoreB } = request;
  const options = [
    { code: match.team_a_code, name: match.team_a },
    { code: match.team_b_code, name: match.team_b },
  ].filter((o): o is { code: string; name: string } => Boolean(o.code && o.name));

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent mx={4}>
          <AlertDialogHeader fontSize="lg">
            ¿Quién avanzó por penales?
          </AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                La quiniela solo cuenta el marcador tras{' '}
                <strong>120 minutos</strong> (tiempo reglamentario + prórroga). Los penales no
                suman goles al pronóstico.
              </Text>
              <Text fontWeight="semibold">
                {match.team_a} {scoreA} – {scoreB} {match.team_b}
                {match.bracket_slot && (
                  <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                    ({match.bracket_slot})
                  </Text>
                )}
              </Text>
              <RadioGroup value={selectedCode} onChange={setSelectedCode}>
                <Stack spacing={2}>
                  {options.map((opt) => (
                    <Radio key={opt.code} value={opt.code}>
                      {opt.name} avanzó
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCancel} isDisabled={isLoading}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isDisabled={!selectedCode}
              isLoading={isLoading}
              onClick={() => onConfirm(selectedCode)}
            >
              Confirmar y guardar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
